import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Phone, MessageSquare, RefreshCw, CheckCircle, XCircle, Clock, Loader2, Settings, Trash2, FileAudio } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RingoverSourcePage() {
  const { personId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [person, setPerson] = useState(null);
  const [ringoverSource, setRingoverSource] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [isConnectOpen, setIsConnectOpen] = useState(false);
  
  const [connectForm, setConnectForm] = useState({
    user_id: '',
    phone_numbers: '',
    sync_calls: true,
    sync_sms: true,
    sync_from_days: 30
  });
  const [ringoverUsers, setRingoverUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const isOwner = user?.role === 'owner';

  useEffect(() => {
    fetchPerson();
    fetchRingoverSource();
    fetchJobs();
  }, [personId]);

  const fetchPerson = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerson(response.data);
    } catch (error) {
      toast.error('Clone not found');
      navigate('/clones');
    }
  };

  const fetchRingoverSource = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}/sources/ringover/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRingoverSource(response.data);
    } catch (error) {
      setRingoverSource(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}/sources/ringover/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.jobs || []);
    } catch (error) {
      // No jobs or not connected
    }
  };

  const handleConnect = async () => {
    try {
      const payload = {
        ...connectForm,
        phone_numbers: connectForm.phone_numbers
          .split(',')
          .map(p => p.trim())
          .filter(p => p)
      };
      
      await axios.post(`${API}/persons/${personId}/sources/ringover/connect`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ringover source connected');
      setIsConnectOpen(false);
      fetchRingoverSource();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect');
    }
  };

  const fetchRingoverUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/persons/sources/ringover/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRingoverUsers(response.data.users || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger les utilisateurs Ringover');
    } finally {
      setLoadingUsers(false);
    }
  };

  const openConnectDialog = () => {
    setIsConnectOpen(true);
    fetchRingoverUsers();
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/persons/${personId}/sources/ringover/sync`, {
        sync_calls: true,
        sync_sms: true,
        max_items: 50
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { calls_processed, sms_processed, memories_created, transcriptions_pending } = response.data;
      toast.success(
        `Sync complete: ${calls_processed} calls, ${sms_processed} SMS processed. ` +
        `${memories_created} memories created. ${transcriptions_pending} transcriptions pending.`
      );
      fetchRingoverSource();
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Ringover source?')) return;
    
    try {
      await axios.delete(`${API}/persons/${personId}/sources/ringover`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ringover source disconnected');
      setRingoverSource(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const statusIcon = {
    pending: <Clock className="w-4 h-4 text-amber-500" />,
    syncing: <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />,
    completed: <CheckCircle className="w-4 h-4 text-emerald-500" />,
    failed: <XCircle className="w-4 h-4 text-rose-500" />
  };

  const jobStatusColors = {
    pending: 'bg-amber-500/10 text-amber-600',
    downloading: 'bg-blue-500/10 text-blue-600',
    transcribing: 'bg-indigo-500/10 text-indigo-600',
    summarizing: 'bg-purple-500/10 text-purple-600',
    completed: 'bg-emerald-500/10 text-emerald-600',
    failed: 'bg-rose-500/10 text-rose-600',
    skipped: 'bg-muted text-muted-foreground'
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout title="Ringover Source">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Ringover Source">
      <div className="max-w-3xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/clones/${personId}/chat`)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-xl font-heading font-bold">Calls & SMS Ingestion</h2>
            <p className="text-sm text-muted-foreground">Ringover integration for {person?.name}</p>
          </div>
        </div>

        {ringoverSource ? (
          <>
            {/* Connected Source */}
            <Card data-testid="ringover-source-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Ringover Connected</CardTitle>
                      <CardDescription>
                        {ringoverSource.sync_calls && 'Calls'} 
                        {ringoverSource.sync_calls && ringoverSource.sync_sms && ' + '}
                        {ringoverSource.sync_sms && 'SMS'}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant={ringoverSource.is_active ? 'default' : 'secondary'}>
                    {ringoverSource.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground mb-1">Sync Status</p>
                    <div className="flex items-center gap-2">
                      {statusIcon[ringoverSource.last_sync_status]}
                      <span className="font-medium capitalize">{ringoverSource.last_sync_status}</span>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground mb-1">Appels</p>
                    <p className="text-2xl font-heading font-bold">{ringoverSource.calls_synced_count}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {ringoverSource.calls_transcribed_count ?? 0} transcrits
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground mb-1">SMS</p>
                    <p className="text-2xl font-heading font-bold">{ringoverSource.sms_synced_count}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground mb-1">Jobs</p>
                    <p className="text-2xl font-heading font-bold">{jobs.length}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {jobs.filter(j => ['pending', 'downloading', 'transcribing', 'summarizing'].includes(j.status)).length} en cours
                    </p>
                  </div>
                </div>

                {/* Last Sync */}
                <div className="flex items-center justify-between py-3 border-t border-b border-border">
                  <div>
                    <p className="text-sm text-muted-foreground">Last sync</p>
                    <p className="font-medium">{formatDate(ringoverSource.last_sync_at)}</p>
                  </div>
                  <Button 
                    onClick={handleSync} 
                    disabled={syncing}
                    className="gap-2"
                    data-testid="sync-btn"
                  >
                    {syncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    {syncing ? 'Syncing...' : 'Sync Now'}
                  </Button>
                </div>

                {/* Error */}
                {ringoverSource.last_sync_error && (
                  <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                    <p className="text-sm text-destructive">{ringoverSource.last_sync_error}</p>
                  </div>
                )}

                <Separator />

                {/* Actions */}
                <div className="flex justify-end">
                  <Button variant="destructive" onClick={handleDisconnect} className="gap-2" data-testid="disconnect-btn">
                    <Trash2 className="w-4 h-4" />
                    Disconnect
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Transcription Jobs */}
            {jobs.length > 0 && (
              <Card data-testid="jobs-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileAudio className="w-5 h-5" />
                    Transcription Jobs
                  </CardTitle>
                  <CardDescription className="flex gap-3">
                    {(() => {
                      const counts = {};
                      jobs.forEach(j => { counts[j.status] = (counts[j.status] || 0) + 1; });
                      return Object.entries(counts).map(([s, c]) => (
                        <span key={s} className="flex items-center gap-1">
                          <span className={`inline-block w-2 h-2 rounded-full ${s === 'completed' ? 'bg-emerald-500' : s === 'failed' ? 'bg-rose-500' : s === 'skipped' ? 'bg-muted-foreground/40' : 'bg-amber-500'}`} />
                          {c} {s}
                        </span>
                      ));
                    })()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {jobs.filter(j => j.status !== 'skipped').slice(0, 10).map((job) => (
                      <div key={job.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                        <div className="flex items-center gap-3">
                          <Badge variant="secondary" className={jobStatusColors[job.status]}>
                            {job.status}
                          </Badge>
                          <div>
                            <p className="text-sm font-medium">Call {job.call_id?.slice(0, 8)}...</p>
                            <p className="text-xs text-muted-foreground">
                              {Math.floor(job.duration_seconds / 60)} min
                            </p>
                          </div>
                        </div>
                        {job.status === 'completed' && (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        )}
                        {job.status !== 'completed' && job.status !== 'failed' && job.status !== 'skipped' && (
                          <Progress value={job.progress_percent} className="w-24" />
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          /* Not Connected */
          <Card data-testid="connect-ringover-card">
            <CardContent className="py-12 text-center">
              <Phone className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No Ringover source connected</h3>
              <p className="text-muted-foreground mb-6">
                Connect Ringover to automatically ingest calls and SMS
              </p>
              
              <Dialog open={isConnectOpen} onOpenChange={setIsConnectOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2" onClick={openConnectDialog} data-testid="connect-ringover-btn">
                    <Phone className="w-4 h-4" />
                    Connect Ringover
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Connecter Ringover</DialogTitle>
                    <DialogDescription>
                      Selectionnez l'utilisateur Ringover a associer a ce clone
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    
                    <div className="space-y-2">
                      <Label>Utilisateur Ringover</Label>
                      {loadingUsers ? (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm text-muted-foreground">Chargement des utilisateurs...</span>
                        </div>
                      ) : ringoverUsers.length > 0 ? (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {ringoverUsers.map(u => (
                            <div
                              key={u.id}
                              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${connectForm.user_id === u.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                              onClick={() => {
                                setConnectForm({
                                  ...connectForm,
                                  user_id: u.id,
                                  phone_numbers: u.numbers.join(', ')
                                });
                              }}
                              data-testid={`ringover-user-${u.id}`}
                            >
                              <div>
                                <p className="font-medium text-sm">{u.name || 'Sans nom'}</p>
                                <p className="text-xs text-muted-foreground">{u.email}</p>
                                {u.numbers.length > 0 && (
                                  <p className="text-xs text-muted-foreground mt-0.5">{u.numbers.join(', ')}</p>
                                )}
                              </div>
                              {connectForm.user_id === u.id && <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                          <p className="text-sm text-amber-700 dark:text-amber-400">
                            Aucun utilisateur trouve. Verifiez que la cle API Ringover est configuree dans Settings.
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Sync Calls</Label>
                      <Switch
                        checked={connectForm.sync_calls}
                        onCheckedChange={(checked) => setConnectForm({ ...connectForm, sync_calls: checked })}
                        data-testid="sync-calls-switch"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Sync SMS</Label>
                      <Switch
                        checked={connectForm.sync_sms}
                        onCheckedChange={(checked) => setConnectForm({ ...connectForm, sync_sms: checked })}
                        data-testid="sync-sms-switch"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Sync from last</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          className="w-20"
                          value={connectForm.sync_from_days}
                          onChange={(e) => setConnectForm({ ...connectForm, sync_from_days: parseInt(e.target.value) })}
                          data-testid="days-input"
                        />
                        <span className="text-muted-foreground">days</span>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsConnectOpen(false)}>Cancel</Button>
                    <Button onClick={handleConnect} data-testid="submit-connect-btn">
                      Connect
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">How it works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium flex-shrink-0">
                1
              </div>
              <div>
                <p className="font-medium">Fetch calls & SMS</p>
                <p className="text-sm text-muted-foreground">Retrieve communications from Ringover API</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium flex-shrink-0">
                2
              </div>
              <div>
                <p className="font-medium">Transcribe with Whisper</p>
                <p className="text-sm text-muted-foreground">Audio recordings are transcribed using OpenAI Whisper</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium flex-shrink-0">
                3
              </div>
              <div>
                <p className="font-medium">Generate summaries</p>
                <p className="text-sm text-muted-foreground">GPT-4o creates intelligent summaries of each call</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium flex-shrink-0">
                4
              </div>
              <div>
                <p className="font-medium">Create memories</p>
                <p className="text-sm text-muted-foreground">Transcriptions become searchable clone memories</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
